import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Grid,
  Chip,
  Alert,
  Button
} from '@mui/material';
import { validateEmail, EMAIL_REGEX, STRONG_EMAIL_REGEX } from '../utils/validation';

const EmailValidationDemo: React.FC = () => {
  const [email, setEmail] = useState('');
  const [validationResult, setValidationResult] = useState<{ isValid: boolean; error?: string } | null>(null);
  const [useStrongValidation, setUseStrongValidation] = useState(false);

  const testEmails = [
    'user@example.com',
    'test.email@domain.co.uk',
    'invalid-email',
    'user@.com',
    'user@domain.',
    'user..name@domain.com',
    'user@domain..com',
    'very.long.email.address.that.exceeds.the.maximum.length.allowed.by.rfc.standards@domain.com',
    'user+tag@domain.com',
    'user_name@domain.com',
    'user-name@domain.com',
    'user.name@domain.com',
    'user@domain-name.com',
    'user@domain_name.com',
    'user@domain-name.co.uk',
    'user@domain-name.com.au',
    '   ', // Just whitespace
    '', // Empty string
    '   user@example.com   ', // With leading/trailing spaces
    'john@example.com', // Duplicate email
    'admin@artful.com' // Duplicate email
  ];

  // Demo existing emails for testing duplicate validation
  const existingEmails = [
    'john@example.com',
    'admin@artful.com',
    'support@artful.com',
    'test@example.com',
    'user@domain.com'
  ];

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value) {
      const result = validateEmail(value, useStrongValidation, existingEmails);
      setValidationResult(result);
    } else {
      setValidationResult(null);
    }
  };

  const handleTestEmail = (emailToTest: string) => {
    setEmail(emailToTest);
    const result = validateEmail(emailToTest, useStrongValidation, existingEmails);
    setValidationResult(result);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Email Validation Demo
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Validation Settings
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Chip 
            label="Basic Validation" 
            color={!useStrongValidation ? 'primary' : 'default'}
            onClick={() => setUseStrongValidation(false)}
            sx={{ mr: 1 }}
          />
          <Chip 
            label="Strong Validation" 
            color={useStrongValidation ? 'primary' : 'default'}
            onClick={() => setUseStrongValidation(true)}
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>Basic Regex:</strong> {EMAIL_REGEX.toString()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Strong Regex:</strong> {STRONG_EMAIL_REGEX.toString()}
        </Typography>
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Existing Emails (for duplicate checking):</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {existingEmails.map((email, index) => (
              <Chip 
                key={index} 
                label={email} 
                size="small" 
                variant="outlined" 
                color="secondary"
              />
            ))}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Test Email Validation
        </Typography>
        
        <TextField
          fullWidth
          label="Enter Email"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          margin="normal"
          error={validationResult ? !validationResult.isValid : false}
          helperText={validationResult?.error}
        />

        {validationResult && (
          <Alert 
            severity={validationResult.isValid ? 'success' : 'error'} 
            sx={{ mt: 2 }}
          >
            {validationResult.isValid 
              ? '✅ Valid email format' 
              : `❌ ${validationResult.error}`
            }
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Test Cases
        </Typography>
        
        <Grid container spacing={1}>
          {testEmails.map((testEmail, index) => {
            const result = validateEmail(testEmail, useStrongValidation, existingEmails);
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleTestEmail(testEmail)}
                  sx={{ 
                    width: '100%', 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    borderColor: result.isValid ? 'success.main' : 'error.main',
                    color: result.isValid ? 'success.main' : 'error.main',
                    '&:hover': {
                      borderColor: result.isValid ? 'success.dark' : 'error.dark',
                      backgroundColor: result.isValid ? 'success.light' : 'error.light',
                      opacity: 0.1
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'left', width: '100%' }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                      {testEmail}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {result.isValid ? 'Valid' : 'Invalid'}
                    </Typography>
                  </Box>
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
};

export default EmailValidationDemo; 